import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleTeasers } from '@/mocks/articles';

export default function Navbar() {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setIsCategoryOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setIsGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const closeAll = () => {
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
    setIsGuideOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link
              to="/"
              className="flex items-center gap-2 group"
              onClick={closeAll}
            >
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-shopping-bag-3-line text-background-50 text-base md:text-lg"></i>
              </div>
              <span className="text-foreground-900 font-heading font-semibold text-base md:text-lg whitespace-nowrap">
                Superbuy Spreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              <Link
                to="/"
                className="text-foreground-700 hover:text-primary-500 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              >
                Home
              </Link>

              <div className="relative" ref={categoryRef}>
                <button
                  onClick={() => {
                    setIsCategoryOpen(!isCategoryOpen);
                    setIsGuideOpen(false);
                  }}
                  className="flex items-center gap-1 text-foreground-700 hover:text-primary-500 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Categories
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isCategoryOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 animate-fade-in">
                    <div className="max-h-80 overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 whitespace-nowrap"
                          onClick={() => setIsCategoryOpen(false)}
                        >
                          <span className="w-7 h-7 flex items-center justify-center bg-accent-100 rounded-md flex-shrink-0">
                            <i className="ri-shopping-bag-3-line text-accent-600 text-xs"></i>
                          </span>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => {
                    setIsGuideOpen(!isGuideOpen);
                    setIsCategoryOpen(false);
                  }}
                  className="flex items-center gap-1 text-foreground-700 hover:text-primary-500 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Guides
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isGuideOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isGuideOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-80 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 animate-fade-in">
                    {articleTeasers.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/article/${article.slug}`}
                        className="block px-4 py-3 text-sm hover:bg-primary-50 transition-colors duration-150"
                        onClick={() => setIsGuideOpen(false)}
                      >
                        <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-1">
                          {article.tag}
                        </span>
                        <div className="text-foreground-700 font-medium text-sm leading-snug hover:text-primary-600 transition-colors">
                          {article.title}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="ml-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Explore Resources
              </a>
            </div>

            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer relative z-50"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl transition-transform duration-200 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-background-50 pt-16 animate-fade-in">
          <div className="h-full overflow-y-auto px-5 py-6">
            <Link
              to="/"
              className="flex items-center gap-3 py-3.5 text-foreground-900 font-semibold text-base"
              onClick={closeAll}
            >
              <i className="ri-home-4-line text-lg text-primary-500"></i>
              Home
            </Link>

            <div className="border-t border-background-200/70 my-3"></div>

            <div>
              <h3 className="px-1 py-2 text-xs font-semibold uppercase tracking-widest text-foreground-400">Categories</h3>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-3 bg-background-100 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-all duration-200"
                    onClick={closeAll}
                  >
                    <span className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                      <i className="ri-shopping-bag-3-line text-accent-600 text-xs"></i>
                    </span>
                    <span className="truncate text-sm font-medium">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 my-4"></div>

            <div>
              <h3 className="px-1 py-2 text-xs font-semibold uppercase tracking-widest text-foreground-400">Guides & Articles</h3>
              <div className="space-y-2">
                {articleTeasers.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="block p-3 bg-background-100 rounded-xl hover:bg-primary-50 transition-all duration-200"
                    onClick={closeAll}
                  >
                    <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-1.5">
                      {article.tag}
                    </span>
                    <div className="text-sm font-medium text-foreground-700 leading-snug">
                      {article.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 my-4"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200"
              onClick={closeAll}
            >
              <i className="ri-shopping-bag-3-line"></i>
              Explore Resources
            </a>

            <p className="text-center text-foreground-400 text-xs mt-6">
              Independent Educational Resource
            </p>
          </div>
        </div>
      )}
    </>
  );
}